import { useState } from 'react';
import PropTypes from 'prop-types';
import useDebounce from 'Hooks/useDebounce';
import useResource from 'Hooks/useResource';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { getEventPropertiesForResourceLogging } from 'Helpers/logging';
import { useMetaFieldsImmediatelyRequired } from 'Hooks/useMetaFieldsImmediatelyRequired';
import { scrollToFirstError } from 'Helpers/metaForm';

function useMetaFormBuilder({
  onDelete,
  onFailure,
  onSubmit,
  onSubmitBefore,
  onChange,
  autoSave,
  inlineSubmit,
  additionalActions,
  resource: resourceProp,
  useResourceArgs,
  exportAttributesAsStrings,
  fieldsImmediatelyRequired,
}) {
  const { initializeResource, resource, setAttribute } = useResource(
    resourceProp,
    useResourceArgs,
  );

  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const {
    eventTrackingNodeRef,
    logResourceEvent,
    logResourceEventSuccess,
    logResourceEventFailure,
    logResourceEventFailureWithError,
  } = useEventTrackingLogEvent();

  useMetaFieldsImmediatelyRequired({
    fieldsImmediatelyRequired, errors, resource, setErrors,
  });

  async function handleSubmit(args = {}) {
    const submitEventName = 'Click Submit in MetaForm';

    const { isAutoSave = null, rejectOnError = false } = args;

    if (!isAutoSave) {
      const eventProperties = getEventPropertiesForResourceLogging(resource);
      logResourceEvent(submitEventName, resource, eventProperties);
    }

    setSaving(true);

    let res;

    try {
      let updatedResource = resource;
      if (onSubmitBefore) {
        updatedResource = onSubmitBefore(resource);
      }
      res = await updatedResource.submit({
        exportArgs: {
          exportAttributesAsStrings,
        },
      });

      if (isAutoSave && !useResourceArgs.allowParentOverride) {
        const [nextResource] = res;
        initializeResource(nextResource);
      }

      if (onSubmit) {
        res = await onSubmit(res, args);
      }

      if (!isAutoSave) {
        const eventProperties = getEventPropertiesForResourceLogging(resource);
        logResourceEventSuccess(submitEventName, resource, eventProperties);
      }

      setErrors([]);
      setSaving(false);
    } catch (err) {
      setSaving(false);
      if (inlineSubmit) {
        // When inlineSubmit, we want to handle the error via the FieldControl tied to the attribute we're submitting from
        // so we throw here to allow our error to bubble up
        setErrors(err.errors);
        if (onFailure) {
          onFailure(err);
        }
        throw err;
      }

      const eventProperties = getEventPropertiesForResourceLogging(resource);
      if (!isAutoSave && err.response) {
        logResourceEventFailureWithError(submitEventName, resource, err, eventProperties);
      } else if (!isAutoSave) {
        logResourceEventFailure(submitEventName, resource, eventProperties);
      }

      if (err.errors && Array.isArray(err.errors)) {
        setErrors(err.errors);
        scrollToFirstError(resource, err.errors);
      }

      if (rejectOnError) {
        throw err;
      }

      if (onFailure) {
        res = onFailure(err);
      }
    }

    return res;
  }

  async function handleAutoSave(att, value) {
    resource.attr(att, value);
    return handleSubmit({
      isAutoSave: true,
    });
  }

  async function handleDelete() {
    let res;

    setSaving(true);

    try {
      res = await resource.delete();
      if (onDelete) {
        res = await onDelete(res, resource);
      }
    } catch (err) {
      if (onFailure) {
        res = await onFailure(err);
      }
    }

    setSaving(false);

    return res;
  }

  const applyAutoSave = useDebounce((att, value) => {
    handleAutoSave(att, value);
  });

  function handleUpdate(attrName, value) {
    setAttribute(attrName, value);
    if (onChange) {
      onChange(attrName, value);
    }
    if (autoSave) {
      applyAutoSave(attrName, value);
    }
    const filteredErrors = errors.filter(err => err.source !== attrName);
    if (filteredErrors.length !== errors.length) {
      setErrors(filteredErrors);
    }
  }

  return {
    eventTrackingNodeRef,
    errors,
    resource,
    saving,
    onChange: handleUpdate,
    onDelete: onDelete ? handleDelete : null,
    onSubmit: handleSubmit,
    additionalActions: additionalActions?.map((action) => {
      const { onClick } = action;
      const onClickWrapper = async (...args) => {
        setSaving(true);
        await onClick(...args);
        setSaving(false);
      };
      return {
        ...action,
        onClick: onClick && onClickWrapper,
      };
    }),
  };
}

useMetaFormBuilder.propTypes = {
  children: PropTypes.node.isRequired,
  onDelete: PropTypes.func,
  onFailure: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  resource: PropTypes.object.isRequired,
  exportAttributesAsStrings: PropTypes.bool,
  fieldsImmediatelyRequired: PropTypes.bool,
};

useMetaFormBuilder.defaultProps = {
  exportAttributesAsStrings: undefined,
  fieldsImmediatelyRequired: false,
};

export default useMetaFormBuilder;
